import AuthService from 'services/Auth';
import { capitalize } from 'lodash';
import { aetherFetch } from './aetherFetch';
import {
  AbstractionType,
  IDocumentAbstractionDocument,
  IDocumentAbstractionPdfWorkflowResult,
  IDocumentAbstractionProject,
  IDocumentAbstractorResponse,
} from '../../interfaces/IAbstraction';

export class AetherApi {
  static async createDocumentAbstractionProject(params: {
    projectName: string;
    oktaUserId: string;
    metadata?: Record<string, unknown>;
  }): Promise<IDocumentAbstractionProject> {
    const res = await aetherFetch('document-abstraction/project', {
      method: 'POST',
      body: JSON.stringify({
        projectName: params.projectName,
        oktaUserId: params.oktaUserId,
        metadata: params.metadata ?? {},
      }),
    });
    if (!res.ok) {
      const detail = await res.text().catch(() => '');
      throw new Error(
        `Aether project create failed (${res.status}): ${detail}`,
      );
    }
    return res.json();
  }

  static async createDocumentAbstractionDocument(params: {
    projectUuid: string;
    file: File;
    documentName: string;
    createdOktaUserId: string;
  }): Promise<IDocumentAbstractionDocument> {
    const form = new FormData();
    form.append('file', params.file);
    form.append('projectUuid', params.projectUuid);
    form.append('documentName', params.documentName);
    form.append('createdOktaUserId', params.createdOktaUserId);
    form.append('documentType', 'PDF');

    const res = await aetherFetch('document-abstraction/document', {
      method: 'POST',
      body: form,
    });
    if (!res.ok) {
      const detail = await res.text().catch(() => '');
      throw new Error(
        `Aether document create failed (${res.status}): ${detail}`,
      );
    }
    return res.json();
  }

  /**
   * Chat upload: 1 create project => 2 create document => 3 parse
   */
  static async runDocumentAbstractionPdfWorkflow(params: {
    pdfFile: File;
    chunkSize: number;
  }): Promise<IDocumentAbstractionPdfWorkflowResult> {
    const { pdfFile, chunkSize } = params;
    const documentName = pdfFile.name || 'document.pdf';
    const { oktaUserId, clientCompany } = AuthService.getLocalUser() ?? {};

    if (!oktaUserId) {
      throw new Error(
        'Okta user id is missing. Sign out and sign in again to refresh your session.',
      );
    }

    const project = await AetherApi.createDocumentAbstractionProject({
      projectName: documentName,
      oktaUserId,
      metadata: { clientId: clientCompany?.id },
    });

    const document = await AetherApi.createDocumentAbstractionDocument({
      projectUuid: project.projectUuid,
      file: pdfFile,
      documentName,
      createdOktaUserId: oktaUserId,
    });
    const parseInfo = await AetherApi.parseDocumentAbstractionDocument(
      document.documentUuid,
      chunkSize,
    );

    const blob = await AetherApi.getDocumentAbstractionDocumentURL(
      document.documentUuid,
    );

    const parse = {
      ...parseInfo,
      url: URL.createObjectURL(blob),
    };

    return { project, document, parse };
  }

  static async parseDocumentAbstractionDocument(
    documentUuid: string,
    chunkSize = 1000,
  ): Promise<{ message: string; document: { tags?: unknown[] } }> {
    const qs = new URLSearchParams({ chunkSize: String(chunkSize) });
    const res = await aetherFetch(
      `document-abstraction/document/${documentUuid}/parse?${qs.toString()}`,
      {
        method: 'POST',
        body: JSON.stringify({ applicableTags: [] }),
      },
    );
    if (!res.ok) {
      const detail = await res.text().catch(() => '');
      throw new Error(
        `Aether document parse failed (${res.status}): ${detail}`,
      );
    }
    return res.json();
  }

  static async runDocumentAbstractor(params: {
    projectUuid: string;
    type: AbstractionType;
  }): Promise<IDocumentAbstractorResponse> {
    const { projectUuid, type } = params;
    const res = await aetherFetch(
      `abstraction/${type}/abstractor/${projectUuid}`,
      {
        method: 'POST',
        body: JSON.stringify({ textResponse: false }),
      },
    );
    if (!res.ok) {
      const detail = await res.text().catch(() => '');
      throw new Error(
        `${capitalize(type)} abstraction failed (${res.status}): ${detail}`,
      );
    }
    return res.json();
  }

  static async sendDocumentAbstractorMessage(params: {
    projectUuid: string;
    type: AbstractionType;
    userInput: string;
    sessionId?: string | null;
    data?: Record<string, unknown> | null;
  }): Promise<IDocumentAbstractorResponse> {
    const res = await aetherFetch(
      `abstraction/${params.type}/chat/${params.projectUuid}`,
      {
        method: 'POST',
        body: JSON.stringify({
          userInput: params.userInput,
          sessionId: params.sessionId ?? null,
          textResponse: false,
          data: params.data ?? null,
        }),
      },
    );
    if (!res.ok) {
      const detail = await res.text().catch(() => '');
      throw new Error(
        `${capitalize(params.type)} abstraction chat failed (${
          res.status
        }): ${detail}`,
      );
    }
    return res.json();
  }

  static async getDocumentAbstractionDocumentURL(
    documentUuid: string,
  ): Promise<Blob> {
    const res = await aetherFetch(
      `document-abstraction/document/${documentUuid}/`,
      {
        method: 'GET',
      },
    );
    if (!res.ok) {
      const detail = await res.text().catch(() => '');
      throw new Error(
        `PDF document retrieval failed (${res.status}): ${detail}`,
      );
    }
    return res.blob();
  }

  static async downloadFolioDocument(documentUuid: string, fileName: string) {
    const res = await aetherFetch(
      `folio/unstructured/document/${documentUuid}/`,
      {
        method: 'GET',
      },
    );
    if (!res.ok) {
      const detail = await res.text().catch(() => '');
      throw new Error(
        `Folio document retrieval failed (${res.status}): ${detail}`,
      );
    }
    const blob = await res.blob();

    // Create a temporary object URL for the blob
    const url = URL.createObjectURL(blob);

    // Create a hidden anchor element to trigger the download
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();

    // Clean up
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }
}
